import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { documentsApi, assetsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useLookup } from '../hooks/useLookups';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../components/ui/command';
import { SlideDrawer } from '../components/ui/slide-drawer';
import { ActionMenu } from '../components/ui/action-menu';
import { DensityToggle, SortableTh, useDensity, useSortState, clientSort } from '../components/ui/list-controls';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { format } from 'date-fns';
import { Check, ChevronsUpDown } from 'lucide-react';
import { 
  Loader2, 
  FileText, 
  Plus,
  Upload,
  Trash2,
  Download,
  CalendarIcon,
  AlertCircle,
  File,
  FileCheck,
  Search,
  LayoutGrid,
  List,
  X,
  Eye,
  User,
  Globe,
  Lock,
  Package
} from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';
import { useConfirm } from '../components/useConfirm';

// Fallback types in case lookup is empty
const DEFAULT_DOC_TYPES = [
  { value: 'certificate', name: 'Certificate', icon: 'FileCheck' },
  { value: 'manual', name: 'Manual', icon: 'FileText' },
  { value: 'inspection_report', name: 'Inspection Report', icon: 'File' },
  { value: 'other', name: 'Other', icon: 'File' },
];

// Icon mapping
const iconMap = {
  FileCheck,
  FileText,
  File,
};

// Helper to get icon component
const getIconComponent = (iconName) => {
  return iconMap[iconName] || File;
};

// Document Upload Form Component
const DocumentUploadForm = ({ assets, docTypes, preSelectedAsset, onSuccess, onCancel }) => {
  const { formatDate } = useSettings();
  const fileInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [expiryDate, setExpiryDate] = useState(null);
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [fileMeta, setFileMeta] = useState(null);   // { name, size }
  const [formData, setFormData] = useState({
    asset_id: preSelectedAsset || '',
    name: '',
    type: docTypes?.[0]?.value || 'certificate',
    file_data: null,
  });

  const selectedAsset = useMemo(
    () => assets.find(a => a.id === formData.asset_id),
    [assets, formData.asset_id]
  );

  // Shared "we got a file" path — used by both the file picker click flow
  // and the drag-and-drop flow so behaviour is identical.
  const acceptFile = (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10 MB');
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => toast.error('Could not read the file');
    reader.onloadend = () => {
      setFormData(prev => ({
        ...prev,
        file_data: reader.result,
        name: prev.name || file.name.replace(/\.[^.]+$/, ''),
      }));
      setFileMeta({ name: file.name, size: file.size, type: file.type });
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e) => acceptFile(e.target.files?.[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    acceptFile(e.dataTransfer.files?.[0]);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.asset_id) {
      toast.error('Please select an asset');
      return;
    }
    if (!formData.file_data) {
      toast.error('Please select a file');
      return;
    }
    if (!formData.name.trim()) {
      toast.error('Document name is required');
      return;
    }

    setSubmitting(true);
    try {
      await documentsApi.upload({
        asset_id: formData.asset_id,
        name: formData.name.trim(),
        type: formData.type,
        file_data: formData.file_data,
        expiry_date: expiryDate ? expiryDate.toISOString() : null,
        file_size: fileMeta?.size ?? null,
        content_type: fileMeta?.type ?? null,
      });
      toast.success('Document uploaded');
      onSuccess();
    } catch (error) {
      // Surface the real backend detail so the user can act on it (size,
      // unsupported type, Azure auth, etc.) instead of a generic toast.
      const detail = error?.response?.data?.detail;
      toast.error(
        typeof detail === 'string'
          ? detail
          : `Failed to upload document${error?.message ? `: ${error.message}` : ''}`
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label>Asset *</Label>
        <Popover open={assetPickerOpen} onOpenChange={setAssetPickerOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={assetPickerOpen}
              className="w-full justify-between font-normal"
              data-testid="doc-asset-select"
            >
              {selectedAsset
                ? `${selectedAsset.unit_number ? selectedAsset.unit_number + ' · ' : ''}${selectedAsset.name}`
                : 'Select asset…'}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-60" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command
              filter={(value, search) => {
                // value is the asset.id; we need to search name + unit number.
                // Resolve back to the asset on each keystroke.
                const a = assets.find(x => x.id === value);
                if (!a) return 0;
                const hay = `${a.name} ${a.unit_number || ''} ${a.serial_number || ''} ${a.make || ''} ${a.model || ''}`.toLowerCase();
                return hay.includes(search.toLowerCase()) ? 1 : 0;
              }}
            >
              <CommandInput placeholder="Search by name, unit #, serial…" data-testid="doc-asset-search" />
              <CommandEmpty>No asset matches.</CommandEmpty>
              <CommandList>
                <CommandGroup>
                  {assets.map(a => (
                    <CommandItem
                      key={a.id}
                      value={a.id}
                      onSelect={(v) => {
                        setFormData(prev => ({ ...prev, asset_id: v }));
                        setAssetPickerOpen(false);
                      }}
                      data-testid={`doc-asset-option-${a.id}`}
                    >
                      <Check
                        className={`mr-2 h-4 w-4 ${formData.asset_id === a.id ? 'opacity-100' : 'opacity-0'}`}
                      />
                      <div className="min-w-0">
                        <p className="truncate">
                          {a.unit_number ? <span className="font-mono text-xs text-volt mr-1.5">{a.unit_number}</span> : null}
                          {a.name}
                        </p>
                        {(a.make || a.serial_number) && (
                          <p className="text-[11px] text-muted-foreground truncate">
                            {[a.make, a.model, a.serial_number].filter(Boolean).join(' · ')}
                          </p>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <div className="space-y-2">
        <Label htmlFor="name">Document Name *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="e.g. Safety Certificate 2024"
          required
          data-testid="doc-name-input"
        />
      </div>

      <div className="space-y-2">
        <Label>Document Type *</Label>
        <Select 
          value={formData.type} 
          onValueChange={(v) => setFormData(prev => ({ ...prev, type: v }))}
        >
          <SelectTrigger data-testid="doc-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {docTypes.map(t => (
              <SelectItem key={t.value} value={t.value}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Expiry Date (optional)</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="expiry-date-picker">
              <CalendarIcon className="mr-2 h-4 w-4" />
              {expiryDate ? formatDate(expiryDate) : 'Select expiry date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={expiryDate}
              onSelect={setExpiryDate}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Dropzone — ref-driven click + drag-and-drop. The previous version
          used a <label htmlFor="file-upload"> which broke whenever this form
          got mounted twice (drawer + inline page) because the duplicate
          input id pointed clicks at the hidden first instance. */}
      <div className="space-y-2">
        <Label>File *</Label>
        <div
          role="button"
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            dragOver
              ? 'border-volt bg-volt/5'
              : 'border-border hover:bg-muted/30'
          }`}
          data-testid="file-dropzone"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={handleFileChange}
            className="hidden"
            data-testid="file-input"
          />
          <Upload className={`w-8 h-8 mx-auto mb-2 ${dragOver ? 'text-volt' : 'text-muted-foreground'}`} />
          {fileMeta ? (
            <>
              <p className="text-sm text-foreground font-medium truncate">{fileMeta.name}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {(fileMeta.size / 1024).toFixed(1)} KB · click to replace, or drop a different file
              </p>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                {dragOver ? 'Drop file here' : 'Click to upload or drag and drop'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">PDF, DOC, or Image (max 10 MB)</p>
            </>
          )}
        </div>
      </div>

      <div className="flex gap-4 pt-4">
        <Button type="submit" className="flex-1 btn-industrial" disabled={submitting} data-testid="submit-doc-btn">
          {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
          {submitting ? 'Uploading...' : 'Upload Document'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

// Document Detail View Component — Gold Standard sheet
const formatBytes = (bytes) => {
  if (bytes == null) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fileFormatLabel = (doc) => {
  if (doc.content_type?.includes('pdf')) return 'PDF';
  if (doc.content_type) return doc.content_type.split('/').pop().toUpperCase();
  const ext = (doc.name || '').split('.').pop();
  return ext ? ext.toUpperCase() : 'FILE';
};

const downloadEntry = (entry, fallbackName) => {
  if (entry?.file_url) {
    window.open(entry.file_url, '_blank');
  } else if (entry?.file_data) {
    const link = window.document.createElement('a');
    link.href = entry.file_data;
    link.download = fallbackName || 'document';
    link.click();
  } else {
    toast.error('No file attached to this version');
  }
};

const DocumentDetailView = ({ document, docTypes, assetName, onDelete, onClose, onRefresh }) => {
  const { formatDate } = useSettings();
  const versionFileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [versionOpen, setVersionOpen] = useState(false);
  const [versionFile, setVersionFile] = useState(null); // { name, size, type, data }
  const [versionNote, setVersionNote] = useState('');
  const [versionExpiry, setVersionExpiry] = useState(document.expiry_date ? new Date(document.expiry_date) : null);
  const typeConfig = docTypes?.find(t => t.value === document.type) || DEFAULT_DOC_TYPES.find(t => t.value === document.type) || DEFAULT_DOC_TYPES[3];

  const daysUntil = document.expiry_date
    ? Math.ceil((new Date(document.expiry_date) - new Date()) / (1000 * 60 * 60 * 24))
    : null;
  const expired = daysUntil != null && daysUntil < 0;
  const expiringSoon = daysUntil != null && daysUntil >= 0 && daysUntil <= 30;

  let banner = null;
  if (document.expiry_date) {
    if (expired) banner = { cls: 'bg-[#FDECEA] border-[#DC2626] text-[#B91C1C]', dot: '#DC2626', label: `Expired ${Math.abs(daysUntil)} days ago` };
    else if (expiringSoon) banner = { cls: 'bg-[#FEF6E0] border-[#D9A400] text-[#92600A]', dot: '#D9A400', label: daysUntil === 0 ? 'Expires today' : `Expires in ${daysUntil} days` };
    else banner = { cls: 'bg-[#EAF6EC] border-[#3E9B4F] text-[#2F7D3A]', dot: '#3E9B4F', label: 'Valid' };
  }

  const sizeLabel = formatBytes(document.file_size);
  const fmt = fileFormatLabel(document);
  const hasFile = document.file_url || document.file_data;
  const versions = [...(document.versions || [])].sort((a, b) => (b.version || 0) - (a.version || 0));

  const details = [
    { label: 'Asset', value: assetName },
    { label: 'Document type', value: typeConfig.name },
    { label: 'Expiry date', value: document.expiry_date ? formatDate(document.expiry_date) : 'No expiry' },
    { label: 'Uploaded by', value: document.uploaded_by_name },
    { label: 'Upload date', value: formatDate(document.created_at) },
    { label: 'File', value: [fmt, sizeLabel].filter(Boolean).join(' · ') || '—' },
    { label: 'Visibility', value: document.is_public ? 'Public on QR page' : 'Internal only' },
  ];

  const openVersionDialog = () => {
    setVersionFile(null);
    setVersionNote('');
    setVersionExpiry(document.expiry_date ? new Date(document.expiry_date) : null);
    setVersionOpen(true);
  };

  const handleVersionPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File size must be less than 10 MB'); return; }
    const reader = new FileReader();
    reader.onerror = () => toast.error('Could not read the file');
    reader.onloadend = () => {
      setVersionFile({ name: file.name, size: file.size, type: file.type, data: reader.result });
    };
    reader.readAsDataURL(file);
  };

  const submitVersion = async () => {
    if (!versionFile) { toast.error('Choose a file for the new version'); return; }
    setUploading(true);
    try {
      await documentsApi.uploadVersion(document.id, {
        file_data: versionFile.data,
        file_size: versionFile.size,
        content_type: versionFile.type,
        note: versionNote.trim() || null,
        expiry_date: versionExpiry ? versionExpiry.toISOString() : '',
      });
      toast.success('New version uploaded');
      setVersionOpen(false);
      onRefresh && (await onRefresh());
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to upload new version');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Title block */}
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-lg border border-[#E6E2D9] bg-white flex items-center justify-center shrink-0">
          <FileText className="w-7 h-7 text-[#C9A400]" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-[22px] font-bold text-[#1A1A1A] leading-tight break-words" data-testid="doc-detail-name">{document.name}</h2>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <Badge variant="outline" className="text-[11px] text-[#57534E] border-[#D4CFC2] bg-white">{typeConfig.name}</Badge>
            <span className="text-[12px] font-mono text-muted-foreground">
              {[fmt, sizeLabel, `v${document.version || 1}`].filter(Boolean).join(' · ')}
            </span>
          </div>
        </div>
      </div>

      {/* Expiry status banner */}
      {banner && (
        <div className={`flex items-center justify-between rounded-lg border-l-4 border px-4 py-3 ${banner.cls}`} data-testid="doc-expiry-banner">
          <span className="inline-flex items-center gap-2 text-[14px] font-semibold">
            <span className="w-2 h-2 rounded-full" style={{ background: banner.dot }} />
            {banner.label}
          </span>
          <span className="text-[13px] font-mono">{formatDate(document.expiry_date)}</span>
        </div>
      )}

      {/* Details card */}
      <div className="id-card p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-[#EFEBE3]">
          <span className="text-[10.5px] font-bold uppercase tracking-[0.11em] text-[#8A857A]">Details</span>
        </div>
        <div>
          {details.map((row, i) => (
            <div key={row.label} className={`flex items-start gap-4 px-4 py-3 ${i < details.length - 1 ? 'border-b border-[#EFEBE3]' : ''}`}>
              <span className="w-36 shrink-0 text-[14px] text-[#6B6558]">{row.label}</span>
              <span className="flex-1 text-[14px] text-[#1A1A1A]">{row.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Version history card */}
      <div className="id-card p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-[#EFEBE3] flex items-center gap-2">
          <span className="text-[10.5px] font-bold uppercase tracking-[0.11em] text-[#8A857A]">Version History</span>
          <span className="text-[12px] text-muted-foreground">{versions.length} {versions.length === 1 ? 'version' : 'versions'}</span>
        </div>
        <div>
          {versions.map((v, i) => {
            const current = v.status === 'current';
            return (
              <div key={v.version} className={`flex items-start gap-3 px-4 py-3 ${i < versions.length - 1 ? 'border-b border-[#EFEBE3]' : ''}`} data-testid={`doc-version-${v.version}`}>
                <span className={`w-9 h-8 shrink-0 rounded-md flex items-center justify-center text-[12px] font-bold font-mono ${current ? 'bg-[#FFF3C4] text-[#7A6100]' : 'bg-[#F3F1EC] text-[#8A857A]'}`}>v{v.version}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-semibold text-[#1A1A1A] truncate">{v.uploaded_by_name || 'Unknown'}</div>
                  <div className="text-[12px] text-muted-foreground font-mono">
                    {v.created_at ? formatDate(v.created_at) : '—'}{current ? ' · current' : ' · superseded'}
                  </div>
                  {v.note && <div className="text-[13px] text-[#4A463E] mt-1 whitespace-pre-wrap" data-testid={`doc-version-note-${v.version}`}>{v.note}</div>}
                </div>
                <Button variant="outline" size="sm" className="shrink-0" onClick={() => downloadEntry(v, document.name)} data-testid={`doc-version-download-${v.version}`}>
                  Download
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer actions */}
      <input ref={versionFileRef} type="file" className="hidden" onChange={handleVersionPick} data-testid="doc-version-file-input" />
      <div className="flex items-center gap-3 pt-2 border-t border-[#EFEBE3]">
        {hasFile && (
          <Button onClick={() => downloadEntry(document, document.name)} className="btn-industrial" data-testid="drawer-download-doc-btn">
            <Download className="w-4 h-4 mr-2" />
            Download
          </Button>
        )}
        <Button variant="outline" onClick={openVersionDialog} data-testid="drawer-upload-version-btn">
          <Upload className="w-4 h-4 mr-2" />
          Upload new version
        </Button>
        <Button variant="outline" onClick={onDelete} className="ml-auto text-[#B91C1C] border-[#F3C4BD] hover:bg-[#FFEDEA]" data-testid="drawer-delete-doc-btn">
          <Trash2 className="w-4 h-4 mr-2" />
          Delete
        </Button>
      </div>

      {/* Upload new version dialog */}
      <Dialog open={versionOpen} onOpenChange={(o) => !uploading && setVersionOpen(o)}>
        <DialogContent data-testid="doc-version-dialog">
          <DialogHeader>
            <DialogTitle>Upload new version</DialogTitle>
            <DialogDescription>The current file becomes v{document.version || 1} (superseded) and this becomes the new current version.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>File</Label>
              <Button type="button" variant="outline" className="w-full justify-start" onClick={() => versionFileRef.current?.click()} data-testid="doc-version-choose-file">
                <Upload className="w-4 h-4 mr-2" />
                {versionFile ? `${versionFile.name} · ${formatBytes(versionFile.size)}` : 'Choose a file…'}
              </Button>
            </div>
            <div className="space-y-1.5">
              <Label>What changed <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Textarea
                value={versionNote}
                onChange={(e) => setVersionNote(e.target.value)}
                placeholder="e.g. Renewed for 2027, updated test results…"
                rows={3}
                data-testid="doc-version-note"
              />
            </div>
            <div className="space-y-1.5">
              <Label>New expiry date <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <div className="flex items-center gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="flex-1 justify-start text-left font-normal" data-testid="doc-version-expiry-picker">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {versionExpiry ? formatDate(versionExpiry) : 'No expiry'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={versionExpiry} onSelect={setVersionExpiry} initialFocus />
                  </PopoverContent>
                </Popover>
                {versionExpiry && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setVersionExpiry(null)} data-testid="doc-version-expiry-clear">Clear</Button>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVersionOpen(false)} disabled={uploading}>Cancel</Button>
            <Button onClick={submitVersion} disabled={uploading || !versionFile} data-testid="doc-version-submit">
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const DocumentsPage = () => {
  const { confirm, dialogProps } = useConfirm();
  const [searchParams] = useSearchParams();
  const assetId = searchParams.get('asset');
  const [documents, setDocuments] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [expiryFilter, setExpiryFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [density, setDensity] = useDensity('documents_density');
  const [groupBy, setGroupBy] = useState('expiry');
  const { sortKey, sortDir, toggleSort } = useSortState();
  const DOC_PAGE_SIZE = 25;
  const [visibleCount, setVisibleCount] = useState(DOC_PAGE_SIZE);
  
  // Drawer states
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState(null); // 'add' | 'view'
  const [selectedDocument, setSelectedDocument] = useState(null);
  
  // Dynamic lookups
  const { data: docTypesData } = useLookup('document_type');
  const docTypes = docTypesData?.length > 0 ? docTypesData : DEFAULT_DOC_TYPES;
  
  const { isAdmin, isOfficeUser } = useAuth();
  const { formatDate } = useSettings();
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [assetId]);

  const loadData = async () => {
    try {
      const params = {};
      if (assetId) params.asset_id = assetId;
      
      const [docsRes, assetsRes] = await Promise.all([
        documentsApi.getAll(params),
        assetsApi.getAll(),
      ]);
      setDocuments(docsRes.data);
      setAssets(assetsRes.data);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id, e) => {
    e?.stopPropagation();
    confirm({
      title: 'Delete this document?',
      description: 'This document will be permanently removed.',
      testId: 'confirm-delete-document',
      onConfirm: async () => {
        try {
          await documentsApi.delete(id);
          toast.success('Document deleted');
          closeDrawer();
          loadData();
        } catch (error) {
          toast.error('Failed to delete document');
          throw error;
        }
      },
    });
  };

  // Phase 50: toggle the public-QR visibility of a document.
  const handleTogglePublic = async (doc, e) => {
    e?.stopPropagation();
    const nextValue = !doc.is_public;
    try {
      await documentsApi.patch(doc.id, { is_public: nextValue });
      toast.success(
        nextValue
          ? `${doc.name} is now visible on the public QR page`
          : `${doc.name} is no longer public`,
      );
      loadData();
    } catch (err) {
      toast.error('Failed to update document');
    }
  };

  const getAssetName = (assetId) => {
    const asset = assets.find(a => a.id === assetId);
    return asset ? asset.name : 'Unknown Asset';
  };

  const getDocTypeConfig = (type) => {
    return docTypes.find(t => t.value === type) || DEFAULT_DOC_TYPES.find(t => t.value === type) || DEFAULT_DOC_TYPES[3];
  };

  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const filteredDocuments = useMemo(() => {
    return documents.filter(doc => {
      const matchesSearch = !search || 
        doc.name.toLowerCase().includes(search.toLowerCase()) ||
        getAssetName(doc.asset_id).toLowerCase().includes(search.toLowerCase());
      const matchesType = typeFilter === 'all' || doc.type === typeFilter;
      const matchesExpiry = expiryFilter === 'all' ||
        (expiryFilter === 'no_expiry' && !doc.expiry_date) ||
        (expiryFilter === 'expired' && doc.expiry_date && isExpired(doc.expiry_date)) ||
        (expiryFilter === 'expiring_soon' && doc.expiry_date && isExpiringSoon(doc.expiry_date)) ||
        (expiryFilter === 'valid' && doc.expiry_date && !isExpired(doc.expiry_date) && !isExpiringSoon(doc.expiry_date));
      return matchesSearch && matchesType && matchesExpiry;
    });
  }, [documents, search, typeFilter, expiryFilter, assets]);

  const typeCounts = useMemo(() => {
    const m = { all: documents.length };
    documents.forEach(d => { m[d.type] = (m[d.type] || 0) + 1; });
    return m;
  }, [documents]);

  const expiryCounts = useMemo(() => {
    const m = { all: documents.length, expired: 0, expiring_soon: 0, valid: 0, no_expiry: 0 };
    documents.forEach(d => {
      if (!d.expiry_date) m.no_expiry++;
      else if (isExpired(d.expiry_date)) m.expired++;
      else if (isExpiringSoon(d.expiry_date)) m.expiring_soon++;
      else m.valid++;
    });
    return m;
  }, [documents]);

  const sortedDocuments = useMemo(() => {
    const arr = [...filteredDocuments];
    if (groupBy === 'by_asset') {
      arr.sort((a, b) => getAssetName(a.asset_id).localeCompare(getAssetName(b.asset_id)));
      return arr;
    }
    if (sortKey) {
      return clientSort(arr, sortKey, sortDir, {
        name: (d) => d.name,
        asset: (d) => getAssetName(d.asset_id),
        type: (d) => getDocTypeConfig(d.type).name,
        expiry: (d) => (d.expiry_date ? new Date(d.expiry_date).getTime() : Infinity),
        uploaded_by: (d) => d.uploaded_by_name,
      });
    }
    arr.sort((a, b) => {
      const at = a.expiry_date ? new Date(a.expiry_date).getTime() : Infinity;
      const bt = b.expiry_date ? new Date(b.expiry_date).getTime() : Infinity;
      return at - bt;
    });
    return arr;
  }, [filteredDocuments, groupBy, sortKey, sortDir, assets]);

  // Gold-standard "Load More" paging (flat view only; grouped shows all).
  const paged = groupBy === 'by_asset' ? sortedDocuments : sortedDocuments.slice(0, visibleCount);
  const hasMore = groupBy !== 'by_asset' && sortedDocuments.length > visibleCount;
  useEffect(() => { setVisibleCount(DOC_PAGE_SIZE); }, [search, typeFilter, expiryFilter, groupBy, sortKey, sortDir]);

  const expiryBadge = (expiryDate) => {
    if (!expiryDate) return { text: 'No expiry', cls: 'text-[#8A857A] bg-[#F3F1EC] border-[#E6E2D9]' };
    const days = Math.ceil((new Date(expiryDate) - new Date()) / 86400000);
    if (days < 0) return { text: `Expired ${Math.abs(days)} days ago`, cls: 'text-[#B91C1C] bg-[#FDECEA] border-[#F3C4BD]' };
    if (days === 0) return { text: 'Expires today', cls: 'text-[#92600A] bg-[#FEF6E0] border-[#EBD48F]' };
    if (days <= 30) return { text: `Expires in ${days} days`, cls: 'text-[#92600A] bg-[#FEF6E0] border-[#EBD48F]' };
    return null;
  };

  const clearFilters = () => {
    setSearch('');
    setTypeFilter('all');
    setExpiryFilter('all');
  };

  const hasActiveFilters = search !== '' || typeFilter !== 'all' || expiryFilter !== 'all';

  const openAddDrawer = () => {
    setSelectedDocument(null);
    setDrawerMode('add');
    setDrawerOpen(true);
  };

  const openViewDrawer = (doc) => {
    setSelectedDocument(doc);
    setDrawerMode('view');
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerMode(null);
    setSelectedDocument(null);
  };

  const handleFormSuccess = () => {
    closeDrawer();
    loadData();
  };

  const getDrawerTitle = () => {
    switch (drawerMode) {
      case 'add': return 'Upload Document';
      case 'view': return selectedDocument?.reference ? `Document ${selectedDocument.reference}` : 'Document Details';
      default: return '';
    }
  };

  const refreshSelectedDocument = async () => {
    if (!selectedDocument) return;
    try {
      const res = await documentsApi.getById(selectedDocument.id);
      setSelectedDocument(res.data);
    } catch { /* keep current */ }
    loadData();
  };

  if (!isOfficeUser()) {
    navigate('/dashboard');
    return null;
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="documents-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-extrabold uppercase tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Document Management
          </h1>
          <p className="text-muted-foreground">Upload and manage asset certificates and documents</p>
        </div>
        <Button onClick={openAddDrawer} className="btn-industrial" data-testid="upload-document-btn">
          <Upload className="w-5 h-5 mr-2" />
          Upload Document
        </Button>
      </div>

      {/* Filters + list — single card (gold standard) */}
      <Card className="overflow-hidden">
        <div className="p-4 border-b border-border space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search documents or assets..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="document-search-input"
              />
            </div>
            <div className="flex items-center gap-3">
              <DensityToggle density={density} onChange={setDensity} />
              <div className="flex items-center gap-0.5 rounded-md bg-secondary p-0.5" data-testid="doc-groupby-toggle">
                {[{ id: 'expiry', label: 'Expiry date' }, { id: 'by_asset', label: 'By asset' }].map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setGroupBy(opt.id)}
                    className={`px-3 h-8 rounded-[6px] text-sm font-medium transition-colors ${groupBy === opt.id ? 'bg-[#FFD108] text-black' : 'text-muted-foreground hover:text-foreground'}`}
                    data-testid={`doc-groupby-${opt.id}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Type + Expiry filter pills */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Type</span>
              <div className="inline-flex items-center gap-1 bg-secondary rounded-lg p-1" data-testid="doc-type-filter">
                {[{ value: 'all', name: 'All types' }, ...docTypes].map(t => {
                  const active = typeFilter === t.value;
                  return (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setTypeFilter(t.value)}
                      data-testid={`doc-type-${t.value}`}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[13px] font-semibold transition-colors ${active ? 'bg-[#FFD108] text-black shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      <span className="uppercase tracking-wide">{t.name}</span>
                      <span className={`text-[11px] font-bold ${active ? 'text-black/70' : 'text-muted-foreground'}`}>{typeCounts[t.value] || 0}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Expiry</span>
              <div className="inline-flex items-center gap-1 bg-secondary rounded-lg p-1" data-testid="doc-expiry-filter">
                {[
                  { id: 'all', label: 'All' },
                  { id: 'expired', label: 'Expired', dot: '#DC2626' },
                  { id: 'expiring_soon', label: 'Expiring soon', dot: '#D9A400' },
                  { id: 'valid', label: 'Valid' },
                  { id: 'no_expiry', label: 'No expiry' },
                ].map(f => {
                  const active = expiryFilter === f.id;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setExpiryFilter(f.id)}
                      data-testid={`doc-expiry-${f.id}`}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[13px] font-semibold transition-colors ${active ? 'bg-[#FFD108] text-black shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      <span className="inline-flex items-center gap-1.5 uppercase tracking-wide">
                        {f.dot && <span className="w-1.5 h-1.5 rounded-full" style={{ background: f.dot }} />}
                        {f.label}
                      </span>
                      <span className={`text-[11px] font-bold ${active ? 'text-black/70' : 'text-muted-foreground'}`}>{expiryCounts[f.id] || 0}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <span className="ml-auto text-xs text-muted-foreground">Expiring soon = within 30 days</span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="py-16 text-center">
            <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Documents Found</h3>
            <p className="text-muted-foreground mb-4">
              {hasActiveFilters ? 'Try adjusting your filters' : 'Upload certificates, manuals, and other asset documents'}
            </p>
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <>
          <div className="overflow-x-auto">
              <table className="data-table w-full" data-density={density}>
                <thead>
                  <tr className="border-b border-border">
                    <SortableTh sortKey="name" label="Document Name" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortableTh sortKey="asset" label="Asset" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortableTh sortKey="type" label="Type" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortableTh sortKey="expiry" label="Expiry Date" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortableTh sortKey="uploaded_by" label="Uploaded By" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <th className="text-right p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((doc, idx, arr) => {
                    const typeConfig = getDocTypeConfig(doc.type);
                    const expired = isExpired(doc.expiry_date);
                    const expiringSoon = isExpiringSoon(doc.expiry_date);
                    const badge = expiryBadge(doc.expiry_date);
                    const assetName = getAssetName(doc.asset_id);
                    const showHeader = groupBy === 'by_asset' && (idx === 0 || getAssetName(arr[idx - 1].asset_id) !== assetName);

                    return (
                      <React.Fragment key={doc.id}>
                      {showHeader && (
                        <tr className="bg-[#FBFAF7] border-b border-[#E6E2D9]" data-testid={`document-group-${doc.asset_id}`}>
                          <td colSpan={6} className="px-4 py-2">
                            <span className="font-semibold text-[#2A2A2A] text-[12px]">{assetName}</span>
                            <span className="text-muted-foreground text-[12px]"> · {arr.filter(x => getAssetName(x.asset_id) === assetName).length} documents</span>
                          </td>
                        </tr>
                      )}
                      <tr 
                        className="border-b border-border hover:bg-secondary/50 cursor-pointer transition-colors"
                        onClick={() => openViewDrawer(doc)}
                        data-testid={`document-row-${doc.id}`}
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-medium truncate">{doc.name}</span>
                            {doc.is_public && (
                              <span
                                title="Visible on the public QR-scan page"
                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#2F7D3A] bg-[#EAF6EC] border border-[#BFE3C6] px-1.5 py-0.5 rounded"
                                data-testid={`doc-public-badge-${doc.id}`}
                              >
                                <Globe className="w-3 h-3" /> Public
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="text-muted-foreground">{assetName}</span>
                        </td>
                        <td className="p-4">
                          <Badge variant="outline" className="text-xs text-[#57534E] border-[#D4CFC2] bg-white">{typeConfig.name}</Badge>
                        </td>
                        <td className="p-4">
                          {doc.expiry_date ? (
                            <div className="space-y-1">
                              <div className={`text-sm ${expired ? 'text-[#B91C1C] font-medium' : expiringSoon ? 'text-[#92600A]' : 'text-[#2A2A2A]'}`}>
                                {formatDate(doc.expiry_date)}
                              </div>
                              {badge && (
                                <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${badge.cls}`}>
                                  <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />{badge.text}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border text-[#8A857A] bg-[#F3F1EC] border-[#E6E2D9]">
                              <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />No expiry
                            </span>
                          )}
                        </td>
                        <td className="p-4">
                          <span className="text-sm text-muted-foreground">{doc.uploaded_by_name}</span>
                        </td>
                        <td className="p-4 text-right">
                          <ActionMenu 
                            actions={[
                              { label: 'View Details', icon: Eye, onClick: () => openViewDrawer(doc) },
                              ...((doc.file_url || doc.file_data) ? [{
                                label: 'Download',
                                icon: Download,
                                onClick: () => {
                                  if (doc.file_url) {
                                    window.open(doc.file_url, '_blank');
                                  } else if (doc.file_data) {
                                    const link = window.document.createElement('a');
                                    link.href = doc.file_data;
                                    link.download = doc.name;
                                    link.click();
                                  }
                                }
                              }] : []),
                              {
                                // Phase 50: toggle public visibility — labels
                                // flip to make the destination state obvious
                                // (avoiding "is this on?" confusion).
                                label: doc.is_public ? 'Make Private' : 'Make Public',
                                icon: doc.is_public ? Lock : Globe,
                                onClick: () => handleTogglePublic(doc),
                              },
                              { label: 'Delete', icon: Trash2, onClick: () => handleDelete(doc.id), variant: 'destructive' },
                            ]}
                            testId={`doc-${doc.id}`}
                          />
                        </td>
                      </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t border-border" data-testid="doc-result-count">
              <span className="text-sm text-muted-foreground">Showing {paged.length} of {sortedDocuments.length} documents</span>
              <div className="flex items-center gap-3">
                {hasMore && (
                  <Button variant="outline" size="sm" onClick={() => setVisibleCount(c => c + DOC_PAGE_SIZE)} data-testid="doc-load-more-btn">
                    Load More ({sortedDocuments.length - paged.length} more)
                  </Button>
                )}
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground hover:text-foreground" data-testid="clear-filters-btn">
                    <X className="w-4 h-4 mr-1" /> Clear Filters
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Slide Drawer */}
      <SlideDrawer
        open={drawerOpen}
        onOpenChange={closeDrawer}
        title={getDrawerTitle()}
        size="lg"
        className={drawerMode === 'view' ? 'sheet-ground' : undefined}
        dismissable={drawerMode === 'view'}
      >
        {drawerMode === 'add' ? (
          <DocumentUploadForm 
            assets={assets}
            docTypes={docTypes}
            preSelectedAsset={assetId}
            onSuccess={handleFormSuccess}
            onCancel={closeDrawer}
          />
        ) : drawerMode === 'view' && selectedDocument ? (
          <DocumentDetailView 
            document={selectedDocument}
            docTypes={docTypes}
            assetName={getAssetName(selectedDocument.asset_id)}
            onDelete={() => handleDelete(selectedDocument.id)}
            onClose={closeDrawer}
            onRefresh={refreshSelectedDocument}
          />
        ) : null}
      </SlideDrawer>
      <ConfirmDeleteDialog {...dialogProps} />
    </div>
  );
};
